<template>
  <div>
    <div class="row">
      <div class="col-12 flip-cards-container">
        <square-option
          v-for="(option, index) in selectedMenuItem"
          :key="index"
          :option="option"
        />
      </div>
      <div class="clearfix" />
    </div>
    <div v-if="canViewAlerts">
      <div class="tag-badge custom-tag-badge">
        <span class="dot custom-dot text-center pl-1">
          <em
            class="orange-icon orange-icon-badge-align fas fa-exclamation-circle"
          />
        </span>
        <span class="tag-badge-title py-1 pl-2 at-badge">Alertas</span>
      </div>
      <div v-if="alerts.length > 0" class="row mx-0">
        <div class="col">
          <div class="mt-5">
            <custom-carousel
              class="custom-carousel__extended"
              :items="alerts"
              :size-per-slide="slideSize"
              @go-to-details="goToDetailsHandler"
            />
          </div>
        </div>
      </div>
      <div
        v-if="alerts.length == 0 && canViewEmptyMessage"
        class="alert-empty-container"
      >
        <div class="alert-empty-container__wrapper">
          <em class="fal fa-unlink" />
          <span>Actualmente no cuenta con alertas en sistema.</span>
        </div>
      </div>
    </div>
    <div class="row mx-0 mt-5">
      <div class="col-12 col-md-4">
        <inbox
          ref="_messagesInbox"
          :download-document="downloadDocument"
          :get-messages="getInboxMessages"
        />
        <confidentiality-agreement
          app-name="RemPro"
          :certification-request="completeUserAgreement"
          :value="needUserCertification"
          @input="userAgreementVisibilityHandler"
        />

        <ImmediateMessagePopup
          id="immediateMessagePopup"
          ref="_immediateMessagePopupRef"
          :items="immediateMessages"
          @message-marked-as-read="onMarkedAsRead"
        />
      </div>
      <div class="col-12 col-md-8">
        <compact-calendar :items="schedule" @onDayChange="onDayChange" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { name as userModuleName } from '@/store/modules/user';
import squareOption from '@/components/common/SquareOption';
import ImmediateMessagePopup from '@/components/common/messages/ImmediateMessagePopup.vue';
import CustomCarousel from 'custom-carousel';
import appServices from '@/services/common/dashboard.api.js';
import circleAlertTypes from '@/utils/constants/circleAlertTypes.js';
import { Inbox, CompactCalendar } from 'operations';
import { ConfidentialityAgreement } from 'mipe-security';
import { completeUserAgreement as _completeUserAgreement } from '@/services/common/user.api.js';
import sessionManager from '@/utils/managers/sessionManager.js';
import sessionKeys from '@/utils/constants/sessionKeys.js';
import locationMessageCode from '@/utils/constants/messageLocationCode.js';
import _ from 'lodash';

//default size per slide
const DEFAULT_SIZE_PER_SLIDE_DESKTOP = 4;
const DEFAULT_SIZE_PER_SLIDE_MOBILE = 1;
const KEY_AGREEMENT_COMPLETED = 'agreement-completed';

//import circle images
import CIRCLE_IMAGE_ONE from '@/assets/img/circle_alert_one.svg';
import CIRCLE_IMAGE_TWO from '@/assets/img/circle_alert_two.svg';
import CIRCLE_IMAGE_THREE from '@/assets/img/circle_alert_three.svg';
import CIRCLE_IMAGE_FOUR from '@/assets/img/circle_alert_four.svg';

const HOUR_FORMAT_AM_PM = 'hh:mm A';

export default {
  name: 'Home',
  components: {
    squareOption,
    CustomCarousel,
    Inbox,
    CompactCalendar,
    ConfidentialityAgreement,
    ImmediateMessagePopup,
  },
  data() {
    return {
      alerts: [],
      schedule: [],
      needUserCertification: false,
      messagesPendingToRead: [],
      immediateMessages: [],
      inboxMessages: [],
    };
  },
  computed: {
    ...mapGetters(userModuleName, ['selectedMenuItem']),
    slideSize() {
      return this.isMobile
        ? DEFAULT_SIZE_PER_SLIDE_MOBILE
        : DEFAULT_SIZE_PER_SLIDE_DESKTOP;
    },
    canViewEmptyMessage() {
      return this.userHasPermissions('menu.dash.alert.viewemptymessage');
    },
    canViewAlerts() {
      return this.userHasPermissions('menu.rempro.home.view.alerts');
    },
  },
  created() {
    this.loadMessages().then(() => {
      if (this.immediateMessages.length > 0) {
        this.$refs._immediateMessagePopupRef.init();
      }
      if (this.inboxMessages.length > 0) {
        this.$refs._messagesInbox.init();
      }
    });
  },
  async mounted() {
    await Promise.all([
      this.getAlerts(),
      this.getSchedule(),
      this.userAgreementVisibilityHandler(),
    ]);
  },
  methods: {
    async getSchedule() {
      const { data } = await appServices.fetchSchedule();
      this.schedule = data.map((item) => {
        const i = { ...item };
        try {
          i.iconRef = require('@/assets/img/' + i.iconRef);
        } catch {
          //Only if image is not available
          i.iconRef = require('@/assets/img/clock.svg');
        }
        i.hourStart = moment(i.hourStartFormatted, HOUR_FORMAT_AM_PM)
          .format(HOUR_FORMAT_AM_PM)
          .toLowerCase();

        return i;
      });
    },
    async onDayChange(value) {
      await this.getSchedule(value);
    },
    async getAlerts() {
      const { data } = await appServices.getDashboardAlerts();
      this.alerts = data.map((item) => {
        item.total = this.$options.filters.numberWithCommas(item.total);
        switch (item.type) {
          case circleAlertTypes.CircleOne:
            item.image = CIRCLE_IMAGE_ONE;
            break;
          case circleAlertTypes.CircleTwo:
            item.image = CIRCLE_IMAGE_TWO;
            break;
          case circleAlertTypes.CircleThree:
            item.image = CIRCLE_IMAGE_THREE;
            break;
          case circleAlertTypes.CircleFour:
            item.image = CIRCLE_IMAGE_FOUR;
            break;
          default:
            item.image = CIRCLE_IMAGE_ONE;
        }
        return item;
      });
    },
    goToDetailsHandler({ alertTypeId }) {
      if (alertTypeId) {
        this.$router.push({
          name: 'alertDetails',
          params: {
            id: alertTypeId,
          },
        });
      }
    },
    userAgreementVisibilityHandler(value) {
      if (value != null) {
        sessionStorage.setItem(KEY_AGREEMENT_COMPLETED, !value);
      }
      this.needUserCertification =
        sessionStorage.getItem(KEY_AGREEMENT_COMPLETED) === 'false';
    },
    completeUserAgreement() {
      return _completeUserAgreement();
    },
    async fetchMessages() {
      const { data } = await appServices.getDashboardMessages();

      if (data && data.length > 0) {
        const readedMessages = sessionManager.get(
          sessionKeys.KEY_MESSAGE_READ,
          '[]'
        );

        if (readedMessages && readedMessages.length > 0) {
          const filteredMessages = data.filter(
            (m) => !readedMessages.some((x) => x == m.messageId)
          );

          this.messagesPendingToRead = [...filteredMessages];
        } else {
          this.messagesPendingToRead = [...data];
        }
      }
    },
    async getMessageData() {
      let messages = (await appServices.getDashboardMessages()).data;

      let immediateMessages = messages.filter(
        (s) =>
          s.messageLocationCode === locationMessageCode.Inicio ||
          s.messageLocationCode === locationMessageCode.InicioConAcuse
      );
      sessionManager.set(sessionKeys.KEY_IMMEDIATE_MESSAGES, immediateMessages);

      let inboxMessages = _.chunk(
        messages.filter(
          (s) => s.messageLocationCode === locationMessageCode.Lateral
        ),
        3
      );
      sessionManager.set(sessionKeys.KEY_INBOX_MESSAGES, inboxMessages);

      sessionManager.set(sessionKeys.KEY_MESSAGES_LOADED, true);
    },
    async loadMessages() {
      let messagesLoaded = sessionManager.get(
        sessionKeys.KEY_MESSAGES_LOADED,
        false
      );
      if (!messagesLoaded) {
        await this.getMessageData();
      }

      this.inboxMessages = sessionManager.get(
        sessionKeys.KEY_INBOX_MESSAGES,
        '[]'
      );
      this.immediateMessages = sessionManager.get(
        sessionKeys.KEY_IMMEDIATE_MESSAGES,
        '[]'
      );
    },
    getInboxMessages() {
      return this.inboxMessages.length > 0 ? this.inboxMessages[0] : [];
    },
    downloadDocument(documentId) {
      return appServices.downloadDocumentByAttachmentId(documentId);
    },
    onMarkedAsRead(messageId) {
      appServices.saveReadMessage(messageId);
    },
  },
};
</script>
<style scoped lang="scss">
.flip-cards-container {
  display: flex;
  flex-wrap: wrap;
}

.flip-cards-container > div {
  min-height: 200px;
}

.tag-badge {
  display: inline-block;
  padding-right: 1em;
  height: 42px;
  background: transparent linear-gradient(90deg, #ffffff 0%, #e8e8e8 100%) 0% 0%
    no-repeat padding-box;
  border-radius: 26px;
  cursor: pointer;
  margin-bottom: 10px;
}

.dot {
  width: 39px;
  height: 39px;
  border-radius: 50%;
  display: inline-block;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  padding-top: 9px;
}
.orange-icon {
  color: #ff7058;
  font-size: 26px;
}

.orange-icon-badge-align {
  vertical-align: top;
  margin-left: -3px;
  margin-top: -3px;
}

.tag-badge-title {
  text-align: left;
  font: Bold 25px/30px Lato;
  letter-spacing: 0;
  color: #11291b;
  display: inline-block;
}

.alert-empty-container {
  box-shadow: 0px 3px 6px #00000029;
  border: 0.5px solid #e2e2e2;
  border-radius: 8px;
  padding: 20px;
  margin-top: 40px;
  &__wrapper {
    background: linear-gradient(to top, #fff, #d9e5df 120%);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 50px 50px 30px 50px;
    em {
      font-size: 70px;
      color: #c5d1cb;
    }
    span {
      font-size: 21px;
      color: #3f5b60;
      margin-top: 20px;
      font-weight: bold;
    }
  }
}

.custom-carousel__extended ::v-deep {
  .indicator-number {
    z-index: 0 !important;
  }
}
</style>
